import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

const stats = [
  { value: "200+", label: "Active Creators" },
  { value: "40+", label: "Issues Per Month" },
  { value: "60k+", label: "In Print Right Now" },
  { value: "0%", label: "Upfront Cost" },
];

export default function ApplyCreatorBanner() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-[#F8F4EE] py-20 md:py-28"
    >
      {/* Thin red rule accent top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C41E3A] to-transparent" />

      <div
        className={`relative z-10 max-w-6xl mx-auto px-6 md:px-10 transition-all duration-1000 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Top label */}
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="h-px w-12 bg-[#C41E3A]" />
          <p className="text-[#C41E3A] text-xs tracking-[0.35em] uppercase font-semibold">
            For Creators Like You
          </p>
          <div className="h-px w-12 bg-[#C41E3A]" />
        </div>

        {/* Main layout: left text + right CTA */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left: headline */}
          <div className="flex-1 text-center lg:text-left">
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1C1C1C] leading-tight mb-5"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Your Stories.<br />
              <em className="italic text-[#C41E3A]">In People&apos;s Hands.</em><br />
              Every Single Issue.
            </h2>
            <p className="text-[#5A5A5A] text-base md:text-lg font-light leading-relaxed max-w-lg mx-auto lg:mx-0">
              You make the content. We make the magazine. Whether you write, shoot, illustrate, or all of the above — if you&apos;ve got a voice, we&apos;ve got a press. No upfront cost, no drama, just your work looking incredible in print.
            </p>
          </div>

          {/* Right: card */}
          <div className="w-full lg:w-auto lg:min-w-[360px]">
            <div className="rounded-2xl border border-[#E8E2DA] bg-white p-8 flex flex-col gap-6">
              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p
                      className="text-3xl font-serif font-semibold text-[#C41E3A]"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {stat.value}
                    </p>
                    <p className="text-[#888888] text-xs mt-1 tracking-wide">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="h-px bg-[#E8E2DA]" />

              {/* Perks list */}
              <ul className="space-y-2.5">
                {[
                  "Zero upfront production cost",
                  "Revenue-share — we only eat when you eat",
                  "Weekly, monthly, or quarterly cadence",
                  "Full design &amp; print management — seriously, all of it",
                  "Distributed globally from day one",
                ].map((perk) => (
                  <li key={perk} className="flex items-start gap-2.5 text-[#4A4A4A] text-sm">
                    <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-[#C41E3A]" />
                    </span>
                    <span dangerouslySetInnerHTML={{ __html: perk }} />
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Link
                to="/apply"
                className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-full bg-[#C41E3A] text-white text-sm font-bold tracking-wide whitespace-nowrap hover:bg-[#A0182E] transition-all duration-200 cursor-pointer"
              >
                Apply to Become a Creator
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>

              <p className="text-[#AAAAAA] text-xs text-center">
                We review every application — usually within 48 hours
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Thin red rule accent bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C41E3A] to-transparent" />
    </section>
  );
}
